import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useT } from '../i18n';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

export default function LivePage() {
    const { liveId } = useParams();
    const t = useT();
    const { user } = useAuth();
    const [live, setLive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [msg, setMsg] = useState('');
    const [count, setCount] = useState(1);
    // broadcast password / adult gating removed
    const polling = useRef(null);

    useEffect(() => {
        let mounted = true;
        api.get(`/live/${liveId}`).then(r => {
            if (!mounted) return;
            setLive(r.data);
            // only fetch messages if not premium or user has access
            if (!r.data.is_premium || r.data.can_watch) {
                fetchMessages();
                polling.current = setInterval(fetchMessages, 3000);
            }
        }).catch(e => {});
        return () => { mounted = false; clearInterval(polling.current); };
    }, [liveId]);

    function fetchMessages() {
        api.get(`/live/${liveId}/messages`).then(r => {
            setMessages(r.data.items || []);
        }).catch((err) => {
            // if unauthorized, clear messages
            if (err && err.response && err.response.status === 403) {
                setMessages([]);
            }
        });
    }

    async function sendMessage(e) {
        e.preventDefault();
        const content = msg.trim();
        if (!content) return;
        if (live && live.is_premium && !live.can_watch) {
            alert('No tens accés per enviar missatges. Compra l\'accés primer.');
            return;
        }
        try {
            await api.post(`/live/${liveId}/chat`, { content });
            setMsg('');
            fetchMessages();
        } catch (err) {
            console.error(err);
        }
    }

    async function buyDiamonds() {
        try {
            // Prefer Checkout Session flow
            const res = await api.post(`/live/${liveId}/create-checkout-session`, { count });
            if (res.data && res.data.url) {
                window.location.href = res.data.url;
                return;
            }
        } catch (err) {
            // Fallback to PaymentIntent/dev flow if Checkout not available
            if (!(err && err.response && err.response.status === 500)) {
                console.error(err);
            }
        }

        // Fallback: try existing buy-diamonds (dev mock or PaymentIntent)
        try {
            const res2 = await api.post(`/live/${liveId}/buy-diamonds`, { count });
            if (res2.data && res2.data.mock) {
                alert('Compra simulada completada');
                fetchMessages();
                return;
            }
            if (res2.data && res2.data.client_secret) {
                alert('Payment intent creat. Completa el pagament amb Stripe al client.');
            } else {
                alert('No s\'ha pogut iniciar la compra.');
            }
        } catch (err2) {
            console.error(err2);
            alert('Error al completar la compra');
        }
    }

    async function buyAccess() {
        try {
            const res = await api.post(`/live/${liveId}/create-access-checkout-session`, {});
            if (res.data && res.data.url) {
                window.location.href = res.data.url;
                return;
            }
            if (res.data && res.data.mock) {
                alert('Compra simulada completada');
                const r = await api.get(`/live/${liveId}`);
                setLive(r.data);
                fetchMessages();
                return;
            }
        } catch (err) {
            console.error(err);
            alert('No s\'ha pogut iniciar la compra');
        }
    }

    return (
        <div className="mx-auto max-w-4xl p-4">
            {!live && <div>Carregant...</div>}
            {live && (
                <div>
                    <h1 className="text-2xl font-bold">{live.title || 'Directe'}</h1>
                    <p className="text-sm text-muted">{live.streamer_name} · {live.status}</p>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                            <div className="h-64 bg-black rounded relative"> {/* placeholder video */}
                                {live && live.is_premium && !live.can_watch ? (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="text-center text-white">
                                            <div className="mb-2">Accés de pagament</div>
                                            <div className="mb-4">Preu: {live.price?.toFixed(2)} EUR</div>
                                            <button onClick={buyAccess} className="bg-[color:var(--alb-sun)] px-3 py-1 rounded">Comprar accés</button>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                            <div className="mt-2">
                                <label className="mr-2">{t('live.send_diamonds') || 'Enviar diamants'}</label>
                                <select value={count} onChange={(e) => setCount(parseInt(e.target.value))} className="border p-1">
                                    <option value={1}>1</option>
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                </select>
                                <button onClick={buyDiamonds} className="ml-2 bg-yellow-400 px-3 py-1 rounded">{t('live.buy') || 'Comprar'}</button>
                            </div>
                            {/* Adult/participant info removed */}
                        </div>
                        <div className="md:col-span-1">
                            <div className="bg-[color:var(--alb-surface)] border p-2 rounded h-64 overflow-auto">
                                <h3 className="font-semibold">{t('live.chat') || 'Xat'}</h3>
                                <div className="mt-2 space-y-2">
                                    {messages.slice(0, 200).map(m => (
                                        <div key={m.msg_id} className="text-sm">
                                            <strong>{m.user_name}</strong>: {m.content}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="mt-2 flex gap-2">
                                <button onClick={() => { if (!user) { alert('Cal entrar per denunciar'); window.location.href = '/entrar'; return; } api.post('/reports', { kind: 'live', target_id: liveId }).then((r) => { if (r.data && r.data.action === 'live_ended') { alert('Directe tancat per denúncies'); window.location.href = '/'; } else alert('Denúncia registrada'); }).catch(() => alert('Error')); }} className="text-red-600">Denunciar directe</button>
                            </div>
                            <form onSubmit={sendMessage} className="mt-2 flex gap-2">
                                <input value={msg} onChange={(e) => setMsg(e.target.value)} className="flex-1 border p-2 rounded" placeholder={t('live.type_message') || 'Escriu un missatge...'} />
                                <button className="bg-[color:var(--alb-sun)] px-3 rounded">{t('live.send') || 'Enviar'}</button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
